import { RailChevronSpacer } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailChevron";
import { RailFilePreviewTooltip } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailHoverPreviewTooltip";
import { useRailRowMultiMenuGate } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailMultiSelectionMenu";
import { RailRowOpenButton } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowOpenButton";
import {
  type RailSelectionSource,
  useRailRowSelection,
  useRegisterRailResource,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  WorkspaceFileRecordMenuSlot,
  type WorkspaceFileRecordMenuSlotProps,
} from "@/pages/workspaces/components/WorkspaceFileRecordMenuSlot";
import { getFileRecordName } from "@/panes/file-record-detail/contentType";
import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import { FileRecordTypeIcon } from "@cohesive/ui-components";
import { useMemo, useState } from "react";

export type FilesRailFileMenuActions = Pick<
  WorkspaceFileRecordMenuSlotProps,
  "onRename" | "onMove" | "onCopy" | "onDelete"
>;

interface FilesRailFileRowProps {
  record: FileRecord;
  active?: boolean;
  /** Rail section this row lives in — drives the source-aware highlight. */
  section?: RailSelectionSource;
  /**
   * When false, a parent list host owns contiguous-selection list-item attrs.
   * Default true for flat lists (favorites / workspace files).
   */
  listItem?: boolean;
  onOpen: () => void;
  menuActions?: FilesRailFileMenuActions;
  /**
   * Row is from "Shared with me" — registered for multi-drag so each selected
   * file keeps its own copy-vs-move semantics.
   */
  sharedSource?: boolean;
  /** Indent by a chevron width so the row aligns with expandable siblings
   *  (folder rows) at the same tree depth. Off in flat lists. */
  withChevronSpacer?: boolean;
  dragRef?: (element: HTMLElement | null) => void;
  dragProps?: React.HTMLAttributes<HTMLElement>;
  isDragging?: boolean;
}

/** Leaf file row — rail styling with file actions menu and optional drag wiring. */
export function FilesRailFileRow({
  record,
  active,
  section = "canonical",
  listItem = true,
  onOpen,
  menuActions,
  sharedSource,
  withChevronSpacer,
  dragRef,
  dragProps,
  isDragging,
}: FilesRailFileRowProps) {
  const menu = useRailRowContextMenu();
  const label = getFileRecordName(record);
  // The preview must drop the moment the user opens the row: Radix's own
  // close-on-pointerdown never fires for the Open pill (it stops pointerdown
  // so the row's drag doesn't arm), and `active` — which suppresses the
  // preview of the open page — only flips once navigation lands. Re-entering
  // the row re-arms the preview (a no-longer-active row can be hovered again).
  const [previewDismissed, setPreviewDismissed] = useState(false);
  const openDismissingPreview = () => {
    setPreviewDismissed(true);
    onOpen();
  };
  const { selected, onActivate, rowKey, selectionAttrs } = useRailRowSelection(
    record.id,
    section,
    !!active,
    "file",
    listItem,
  );

  const registered = useMemo(
    () => ({
      kind: "file" as const,
      data: record,
      ...(sharedSource ? { sharedSource: true as const } : {}),
    }),
    [record, sharedSource],
  );
  useRegisterRailResource("file", record.id, registered);

  const onContextMenu = useRailRowMultiMenuGate(rowKey, menu.onContextMenu);

  return (
    // The whole row is the preview trigger: the preview's position then keys
    // off the row's full width (→ the rail's right edge), never overlapping
    // the trailing Open pill / kebab the title button stops short of.
    <RailFilePreviewTooltip
      record={record}
      label={label}
      active={active}
      suppress={menu.open || previewDismissed}
    >
      <div
        ref={dragRef}
        data-rail-file-row
        {...selectionAttrs}
        onContextMenu={onContextMenu}
        onPointerEnter={() => setPreviewDismissed(false)}
        className={cn(
          "group/row relative flex min-h-7 w-full items-center gap-2.5 px-2 pr-8 hover:bg-n-05",
          !selected && "rounded-md",
          selected && "bg-foreground/[0.04] text-n-90",
          isDragging && "cursor-grabbing opacity-40",
        )}
        {...dragProps}
      >
        <button
          type="button"
          // Files have nothing to expand: single click selects, double click
          // opens — one gesture set across the rail and the detail-page grids.
          onClick={(e) => onActivate(e)}
          onDoubleClick={(e) => {
            onActivate(e);
            openDismissingPreview();
          }}
          // self-stretch: keep the full row height clickable.
          className="flex min-w-0 flex-1 select-none items-center gap-2.5 self-stretch text-left outline-none focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]"
        >
          {/* Mirrors RailDisclosureIcon's [chevron gap-1.5 icon] cluster so the
              icon lines up with expandable siblings' icons. */}
          <span className="flex shrink-0 items-center gap-1.5">
            {withChevronSpacer && <RailChevronSpacer />}
            <FileRecordTypeIcon
              contentType={record.content_type}
              originType={record.origin_type}
              className="size-4 shrink-0"
            />
          </span>
          <span className="min-w-0 flex-1 truncate text-base text-n-80">
            {label}
          </span>
        </button>

        {/* Mirrors the double-click gesture: select, then open. */}
        <RailRowOpenButton
          label={label}
          onOpen={() => {
            onActivate();
            openDismissingPreview();
          }}
        />

        {/* The menu always renders: WorkspaceFileRecordMenuSlot carries the
          Star/Unstar + download items independent of the rename/move/copy/delete
          handlers (which the favorites group, spanning workspaces, omits).
          `menuController` routes it through the shared RailRowMenu, which
          positions the kebab and opens at the cursor on right-click. */}
        <WorkspaceFileRecordMenuSlot
          record={record}
          onOpen={openDismissingPreview}
          onRename={menuActions?.onRename}
          onMove={menuActions?.onMove}
          onCopy={menuActions?.onCopy}
          onDelete={menuActions?.onDelete}
          actionScope={
            section === "favorites"
              ? "favorites"
              : sharedSource
                ? "shared-with-me"
                : "canonical"
          }
          railTrigger
          menuController={menu}
        />
      </div>
    </RailFilePreviewTooltip>
  );
}
